import { Children, isValidElement, useEffect, useState } from "react";
import { FaCopy, FaDownload } from "react-icons/fa";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { safeCopy, toolStorage } from "../_UTILS/toolsStorage.util";

export const usePersistentState = (key, initialValue) => {
    const [value, setValue] = useState(() => toolStorage.get(key, initialValue));
    useEffect(() => { toolStorage.set(key, value); }, [key, value]);
    return [value, setValue];
};

export const ToolCard = ({ title, description = null, actions = null, children, className = "" }) => (
    <section className={`LStudio__tools_card ${className}`.trim()}>
        {(title || actions) && (
            <div className="LStudio__tools_card_head">
                <div>
                    {title && <strong>{title}</strong>}
                    {description && <small>{description}</small>}
                </div>
                {actions && <div className="LStudio__tools_card_actions">{actions}</div>}
            </div>
        )}
        <div className="LStudio__tools_card_body">{children}</div>
    </section>
);

export const Field = ({ label, hint = null, children, className = "" }) => (
    <label className={`LStudio__tools_field ${className}`.trim()}>
        {label && <span>{label}</span>}
        {children}
        {hint && <small>{hint}</small>}
    </label>
);

export const TextInput = (props) => <input className="LStudio__tools_input" {...props} />;
export const TextArea = (props) => <textarea className="LStudio__tools_textarea" {...props} />;
const onSelectOptions = (children) => Children.toArray(children).map((child) => {
    if (!isValidElement(child)) return null;
    if (child.type === "optgroup") return {
        label: child.props.label,
        options: Children.toArray(child.props.children).filter(isValidElement).map((option) => ({
            value: option.props.value ?? option.props.children,
            label: option.props.children,
            isDisabled: option.props.disabled,
        })),
    };
    return { value: child.props.value ?? child.props.children, label: child.props.children, isDisabled: child.props.disabled };
}).filter(Boolean);
export const SelectInput = ({ children, value, onChange, disabled = false, ...props }) => <div className="LStudio__tools_select_app"><FieldSelect value={value} onChange={onChange} options={onSelectOptions(children)} isSearchable={false} isDisabled={disabled} {...props} /></div>;

export const CopyButton = ({ value, label = "Copiar" }) => {
    const [copied, setCopied] = useState(false);
    const onCopy = async () => {
        const ok = await safeCopy(value);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
    };
    return <AppButton size="sm" variant="outline" tone="neutral" icon={<FaCopy />} onClick={onCopy}>{copied ? "Copiado" : label}</AppButton>;
};

export const downloadText = (filename, content, type = "text/plain;charset=utf-8") => {
    const blob = new Blob([String(content ?? "")], { type });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

export const DownloadButton = ({ filename, value, label = "Descargar", type }) => (
    <AppButton size="sm" variant="outline" tone="neutral" icon={<FaDownload />} onClick={() => downloadText(filename, value, type)}>{label}</AppButton>
);

export const CodeBox = ({ value, empty = "Sin resultado", copy = true, className = "" }) => (
    <div className={`LStudio__tools_codebox ${className}`.trim()}>
        <pre>{value || empty}</pre>
        {copy && value && <div className="LStudio__tools_codebox_action"><CopyButton value={value} /></div>}
    </div>
);

export const Notice = ({ children, tone = "info" }) => <div className="LStudio__tools_notice" data-tone={tone}>{children}</div>;

export const parseKeyValueLines = (text = "") => String(text).split(/\r?\n/).reduce((acc, line) => {
    const clean = line.trim();
    if (!clean || clean.startsWith("#")) return acc;
    const separator = clean.indexOf("=");
    if (separator < 1) return acc;
    acc[clean.slice(0, separator).trim()] = clean.slice(separator + 1).trim();
    return acc;
}, {});

export const approximateTokens = (text = "") => {
    const value = String(text ?? "");
    if (!value) return 0;
    return Math.max(1, Math.ceil(value.length / 4));
};
